import { ColumnDef } from '@tanstack/react-table'
import { FC } from 'react'
import { formatDuration } from '~/helpers/dateHelper'

import { startCase } from '@hap/utils'

import { MakeColumnOptions } from './types'

type TimeUnit = 'seconds' | 'minutes' | 'mm:ss'

interface Props {
  time: number | string
  unit: TimeUnit
  treatZeroAsNull: boolean
}

interface ColumnOptions<D extends UnknownRecord> extends MakeColumnOptions<D> {
  unit?: TimeUnit
  treatZeroAsNull?: boolean
}

export const DurationCell: FC<Props> = ({
  time = 0,
  unit = 'seconds',
  treatZeroAsNull,
}) => {
  const seconds = unit === 'minutes' ? parseFloat(`${time}`) / 60 : time
  const date = treatZeroAsNull && time === 0 ? '-' : formatDuration(seconds)
  return <span className="whitespace-nowrap font-mono">{date}</span>
}

export function makeDurationColumnV2<D extends UnknownRecord>({
  fieldKey,
  header = startCase(fieldKey.toString()),
  sortable = true,
  unit = 'seconds',
  width = 80,
  minWidth = width,
  maxWidth,
  treatZeroAsNull = false,
  align = 'right',
  footer,
}: ColumnOptions<D>): ColumnDef<D> {
  return {
    accessorKey: fieldKey,
    id: fieldKey as string,
    cell: (cell) => {
      const time = parseInt(`${cell.getValue()}`)
      return (
        <DurationCell
          time={isNaN(time) ? 0 : time}
          unit={unit}
          treatZeroAsNull={treatZeroAsNull}
        />
      )
    },
    header,
    enableSorting: sortable,
    size: width,
    minSize: minWidth,
    maxSize: maxWidth,
    meta: {
      align: align,
    },
    footer: footer
      ? () => {
          const time = parseInt(`${footer}`)
          return (
            <DurationCell
              time={isNaN(time) ? 0 : time}
              unit={unit}
              treatZeroAsNull={treatZeroAsNull}
            />
          )
        }
      : undefined,
  }
}
